import React from 'react';
import Reveal from './Reveal';

/**
 * "Does this sound familiar?" problem grid shared by the live Bootcamp page
 * and the Membership page. One markup and style owner (CoursePage.css
 * .cp-problem*); each page supplies its own lede and closing line.
 */
export const PAIN_POINTS = [
  {
    img: '/images/courses/problem-scan-time.webp',
    title: 'Research eats hours before you trade',
    problem:
      'Tickers, news, levels, endless tabs. Traders on Reddit and X keep saying AI cut that grind from hours to minutes.',
    solve:
      'We teach you a Claude, Codex, Cursor, or BotSpot scan workflow that builds a short, broker-checked list so research stops eating your day.',
  },
  {
    img: '/images/courses/problem-ai-wrong-price.webp',
    title: 'ChatGPT gave you a price. Your broker disagreed.',
    problem:
      'The model sounds sure, then the number is stale or off by a few ticks. Close enough to trust. Wrong enough to hurt.',
    solve:
      'We show you how to connect to your real broker so prices, positions, and account value come from the account you trade, not a chatbot guess.',
  },
  {
    img: '/images/courses/problem-luck-or-edge.webp',
    title: 'Your AI bot had a green week. Skill or luck?',
    problem:
      'A short win streak is not proof. Traders keep asking the same thing: was that edge, or a fluke?',
    solve:
      'We show you how to backtest and run evals on an AI trading agent so you can tell a lucky streak from a process that holds up.',
  },
  {
    img: '/images/courses/problem-chart-vs-broker.webp',
    title: 'TradingView is great for charts. Painful for trades.',
    problem:
      'Broker connections drop, sessions log you out, and a lot of people still chart in one place and click the order somewhere else.',
    solve:
      'We show you how to use Claude, Codex, Cursor, or BotSpot so chart work and a broker-ready trade live in the same agent workflow.',
  },
  {
    img: '/images/courses/problem-pine-locked.webp',
    title: 'AI wrote your Pine Script. You still do not trust it.',
    problem:
      'Anyone can paste a prompt and get an indicator. The hard part is knowing what it measures, when it fails, and whether you would trade it with real money.',
    solve:
      'We teach you to generate, stress-test, and use indicators with Claude, Codex, Cursor, or BotSpot so you ship something you understand.',
  },
  {
    img: '/images/courses/problem-scanner-homework.webp',
    title: 'Building a scanner feels like homework',
    problem:
      'Platform script errors, outdated tutorials, and “too complex” walls show up the second you try to scan the market your way.',
    solve:
      'You build scanners in plain English with Claude, Codex, Cursor, or BotSpot, then verify names against real market data instead of wrestling platform code.',
  },
  {
    img: '/images/courses/problem-options-legs.webp',
    title: 'An iron condor should not take ten clicks and a prayer',
    problem:
      'Multi-leg options are tedious to build, fill, and manage in broker UIs. Traders talk about partial fills and babysitting every leg.',
    solve:
      'You describe the structure in plain English, review the legs with AI, then send a checked order through your broker instead of clicking each leg by hand.',
  },
  {
    img: '/images/courses/problem-talk-to-tools.webp',
    title: 'You wish you could just tell Claude what to do',
    problem:
      'Research, scan, review a trade, repeat. In plain English. Not another maze of menus, scripts, and disconnected screens.',
    solve:
      'That is the core of the cohort: Claude, Codex, Cursor, BotSpot, and your broker working as one command seat you control.',
  },
];

const PainPoints = ({
  points = PAIN_POINTS,
  lede,
  turn,
  heading = 'Does this sound familiar?',
}) => (
  <section className="cp-section cp-problem" data-testid="pain-points">
    <Reveal>
      <h2>{heading}</h2>
    </Reveal>
    {lede ? (
      <Reveal delay={40}>
        <p className="cp-problem-lede">{lede}</p>
      </Reveal>
    ) : null}
    <div className="cp-problem-grid">
      {points.map((pain, index) => (
        <Reveal key={pain.title} delay={Math.min(index * 50, 280)}>
          <article className="cp-problem-card">
            <img
              className="cp-problem-img"
              src={pain.img}
              alt={pain.title}
              loading="lazy"
              width="1280"
              height="714"
            />
            <div className="cp-problem-copy">
              <h3 className="cp-visually-hidden">{pain.title}</h3>
              <p className="cp-problem-body">{pain.problem}</p>
              <p className="cp-problem-fix-label">How we fix this</p>
              <p className="cp-problem-solve">{pain.solve}</p>
            </div>
          </article>
        </Reveal>
      ))}
    </div>
    {turn ? (
      <Reveal delay={300}>
        <p className="cp-turn">{turn}</p>
      </Reveal>
    ) : null}
  </section>
);

export default PainPoints;
